import React, { useState, useEffect } from 'react'
import {
	Row,
	Col,
	Button,
	Form,
	Alert
} from 'react-bootstrap'
import styles from './UpdateUser.module.scss'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import AuthenticationService from '../../services/authenticationService'
import Toast from '../../components/toasts/Toast'

const ProfileSettings = () => {
	const [inputs, setInputs] = useState({
		name: '',
		contact: '',
		email: '',
		password: '',
		newPassword: '',
		confirmPassword: ''
	})

	const user = JSON.parse(localStorage.getItem('E2ICurrentUser') as string)
	const id = user?.id
	const { t } = useTranslation()
	const [updateSuccess, setUpdateSuccess] = useState(false)
	const [updateFailed, setUpdateFailed] = useState(false)
	const [wrongPassword, setWrongPassword] = useState(false)

	const [passwordMismatch, setPasswordMismatch] = useState(false)

	const validationSchema = Yup.object().shape({
		name: Yup.string().min(2, t('Too_short')).max(50, t('Too_Long')).required(t('required.field')),
		contact: Yup.number().min(8, t('At_least_8_number')).typeError(t('This_must_be_a_number')),
		password: Yup.string().required(t('required.field')).typeError('This must be filled in to change your settings'),
		newPassword: Yup.string().min(8, 'Must contain at least 8 characters')
	})

	const handleSubmit = async (values: { name: string; contact: string; email: string; password: string; newPassword: string; confirmPassword: string; }) => {
		// wip
		if (values.newPassword || values.confirmPassword) {
			if (values.newPassword !== values.confirmPassword) {
				setPasswordMismatch(true)
				return
			}
		}
		try {
			const { updated, name, message } = await AuthenticationService.changeSettings({
				loggedInUserId: id,
				name: values.name,
				contact: values.contact,
				email: values.email,
				password: values.password,
				newPassword: values.newPassword,
				confirmPassword: values.confirmPassword
			})
			if (updated) {
				const obj = {
					id: id,
					name: name
				}
				localStorage.setItem('E2ICurrentUser', JSON.stringify(obj))
				setUpdateSuccess(true)
				setUpdateFailed(false)
			} else if (!updated && message) {
				setUpdateSuccess(false)
				setWrongPassword(true)
			}
			else {
				setUpdateSuccess(false)
				setUpdateFailed(true)
			}
		} catch (err) {
			return console.log(err)
		}
	}

	useEffect(() => {

		const getUserProfile = async () => {
			try {
				const res = await axios.get(process.env.REACT_APP_ROUTE_CRUD_PROFILE as string + '/' + id)
				setInputs({
					...inputs,
					name: res.data.user[0].name ?? '',
					contact: res.data.user[0].contact ?? '',
					email: res.data.user[0].email
				})
			} catch (err) {
				return console.log(err)
			}
		}
		getUserProfile()

	}, [])

	return (
		<div>
			<Formik
				enableReinitialize
				initialValues={inputs}
				validationSchema={validationSchema}
				onSubmit={values => {
					handleSubmit(values)
				}}
			>
				{({ values, handleChange, errors, touched }) => (
					<FormikForm>

						<Form.Group className='mb-4 mt-5'>
							<Col md={4}>
								<Form.Label>Name</Form.Label>
								<Form.Control
									className={styles.formControl}
									type='text'
									placeholder={t('profile_personal.enter_name')}
									name='name'
									value={values.name}
									onChange={handleChange}
								/>
								{errors.name && touched.name && <div className={styles.errMessage}
								>{errors.name}</div>}
							</Col>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Col md={4}>
								<Form.Label>Contact Number</Form.Label>
								<Form.Control
									className={styles.formControl}
									placeholder='Enter your contact number'
									name='contact'
									value={values.contact}
									onChange={handleChange}
									minLength={8} maxLength={8}
								/>
								{errors.contact && touched.contact && <div className={styles.errMessage}
								>{errors.contact}</div>}
							</Col>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Col md={4}>
								<Form.Label>{t('profile.email')}</Form.Label>
								<span className={styles.mandatory}>*</span>
								<Form.Control
									className={styles.formControl}
									type='email'
									placeholder={t('sign_up.email_plhlder')}
									name='email'
									value={values.email}
									onChange={handleChange}
									required
								/>
								{errors.email && touched.email && <div className={styles.errMessage}
								>{errors.email}</div>}
							</Col>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Col md={4}>
								<Form.Label>{t('login.password')}</Form.Label>
								<span className={styles.mandatory}>*</span>
								<Form.Control
									className={styles.formControl}
									type='password'
									placeholder='Enter your current password'
									name='password'
									value={values.password}
									onChange={handleChange}
									required
								/>
								{errors.password && touched.password && <div className={styles.errMessage}
								>{errors.password}</div>}
							</Col>
						</Form.Group>

						<Form.Group className='mb-3'>
							<Col md={4}>
								<Form.Label>New Password</Form.Label>
								<Form.Control
									className={styles.formControl}
									type='password'
									placeholder='Enter your new password'
									name='newPassword'
									value={values.newPassword}
									onChange={(e) => {
										setPasswordMismatch(false)
										setUpdateSuccess(false)
										setUpdateFailed(false)
										handleChange(e)
									}}
								/>
								{errors.newPassword && touched.newPassword && <div className={styles.errMessage}
								>{errors.newPassword}</div>}
							</Col>
						</Form.Group>

						<Form.Group className='mb-5'>
							<Col md={4}>
								<Form.Label>Confirm Password</Form.Label>
								<Form.Control
									className={styles.formControl}
									type='password'
									placeholder='Confirm your new password'
									name='confirmPassword'
									value={values.confirmPassword}
									onChange={(e) => {
										setPasswordMismatch(false)
										setUpdateSuccess(false)
										setUpdateFailed(false)
										handleChange(e)
									}}
								/>
							</Col>
						</Form.Group>
						<Col md={4}>
							{passwordMismatch && <Alert variant='danger'>The two passwords that you enter is inconsistent</Alert>}
							<Toast
								show={updateSuccess}
								setShow={setUpdateSuccess}
								position={'top-end'}
								headerText='Successful!'
								bodyText='Your profile has been updated'
								textColor='green' />
							<Toast
								show={updateFailed}
								setShow={setUpdateFailed}
								position={'top-end'}
								headerText='Unsuccessful'
								bodyText='Update failed, please try again'
								textColor='red' />
							<Toast
								show={wrongPassword}
								setShow={setWrongPassword}
								position={'top-end'}
								headerText='Unsuccessful'
								bodyText='Wrong password'
								textColor='red' />
						</Col>
						<div>
							<Col md={4}>
								<Row>
									<Col>
										<Button
											className={styles.updateUser}
											variant='primary'
											type='submit'
										>{t('submit')}</Button>
									</Col>
								</Row>
							</Col>

						</div>
					</FormikForm>
				)}
			</Formik>

		</div>
	)
}

export default ProfileSettings